import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import OwnerSelectComponent from 'webapp/components/owner-select';
import type { Option } from 'webapp/components/reports/index/item-base';
import ReportsIndexItemBaseComponent from 'webapp/components/reports/index/item-base';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TagContainerComponent from 'webapp/components/tag-container';
import TeamsViewSectionRestrictedIconComponent from 'webapp/components/teams/view/section/restricted-icon';
import ToolTipComponent from 'webapp/components/tool-tip';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

export default class ReportsIndexGridItemComponent extends ReportsIndexItemBaseComponent {
	@service('popup') declare popupService: Service.Popup;

	@tracked overlayActive = false;

	get hasNoOwner(): boolean {
		return !this.args.model.ownerUser;
	}

	get additionalOptions(): Option[] {
		const shownOutsideDropdown = ['view', 'edit', 'share', 'toggleFavorite', 'restore'],
			teamSectionItemOptions = this.teamSectionItemOptions;

		const options = this.options.filter((item) => {
			return item.action && !shownOutsideDropdown.includes(item.action);
		});

		options.push(...teamSectionItemOptions);

		return options;
	}

	get canEdit(): boolean {
		return this.canCurrentUserEdit && !this.isDemoAccount;
	}

	get checkVisibility(): boolean {
		return !!this.args.checkVisibility;
	}

	get showDragHandle(): boolean {
		return this.canEdit && !this.isDeleted;
	}

	@action
	openAdditionalOptions(e: PointerEvent): void {
		if (this.overlayActive) {
			return;
		}

		const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>('.context');
		const popup = this.popupService.createListPopup(
			attachTo,
			undefined,
			'board-options',
			true,
			true,
		);

		if (popup) {
			popup.open({
				itemsList: this.additionalOptions,
				doAction: (actionName: string, id: string) => {
					this.doOptionsAction(actionName, id);
				},
			});

			this.overlayActive = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.overlayActive = false;
				}
			});
		}
	}

	@action
	override removeFromSection(): void {
		const teamSectionItem = this.args.teamSectionItem;

		if (teamSectionItem) {
			this.args.removeFromSection?.(teamSectionItem);
		}
	}
	<template>
		<li
			data-id={{@model.id}}
			class={{joinClasses
				(array
					"card"
					"card--preview"
					"card--report"
					"flex-item"
					"filter-sort-item"
					@model.animationClass
					(if this.overlayActive "card--show-overlay")
					(if @hideSort "hideSort")
					(if this.isSelected "selected")
				)
			}}
		>

			<BoardImagePreviewComponent
				@image={{@model.image}}
				@fallbackImage={{@model.fallbackImage}}
				@color={{@model.color}}
				@theme={{@model.bgTheme}}
				@isVisibleOnScreen={{this.isVisibleOnScreen}}
				@isLazyLoaded={{true}}
			/>

			{{#if this.isVisibleOnScreen}}

				<div class="overlay">

					<div class="overlay-bg"></div>

					{{#if this.showDragHandle}}
						<div class="drag" data-id={{@model.id}}>
							<CommonUiIconComponent @name="ArrowsMove" @size="md" />
						</div>
					{{/if}}

					<div class="wrap">
						<div class="actions">
							<div class="actions__top">

								{{#if this.isDeleted}}
									{{#if this.canCurrentUserEditWithoutDeleted}}
										<SubmitButtonComponent
											@text="Restore"
											@extraClass="previewbutton"
											@onTrigger={{this.restore}}
											@noDelay={{true}}
										/>
									{{/if}}
								{{else}}
									<SubmitButtonComponent
										@text="View"
										@extraClass="previewbutton"
										@onTrigger={{this.view}}
										@noDelay={{true}}
									/>

									{{#if this.canCurrentUserEdit}}
										<SubmitButtonComponent
											@text="Edit"
											@icon="Pencil"
											@extraClass="editbutton"
											@onTrigger={{this.edit}}
											@noDelay={{true}}
											@theme="grey-outlined"
										/>
									{{/if}}
								{{/if}}
							</div>
							{{#if this.showActions}}
								{{#if this.canCurrentUserEdit}}
									<div class="actions__bottom">
										<SubmitButtonComponent
											@text="Share"
											@icon="Share"
											@onTrigger={{this.share}}
											@noDelay={{true}}
											@theme="grey-outlined"
										/>
									</div>
								{{/if}}
							{{/if}}
						</div>
					</div>

				</div>

				<div class="top">
					<div class="top__left">
						{{#if this.emblemTypeName}}
							<div class="emblem emblem--{{this.emblemTypeName}}"></div>
						{{/if}}

						{{#if @batchSelect}}
							<div class="checkbox-wrapper">
								<FormCheckboxComponent
									@name="reports-grid-item"
									@selected={{this.isSelected}}
									@theme="blue"
									@onChange={{this.selectItem}}
								/>
							</div>
						{{/if}}
					</div>

					{{#if this.showActions}}
						<div class="top__right">

							{{#if @isRestricted}}
								<TeamsViewSectionRestrictedIconComponent />
							{{/if}}
							{{#unless this.isDeleted}}
								<VerificationBadgeComponent
									@verification={{@model.verificationInfo}}
									@entityLabel="report"
									@size="sm"
								/>
								<div
									role="button"
									class="favorite"
									{{on "click" this.toggleFavorite}}
								>
									<CommonUiIconComponent
										@name="StarFill"
										@size="sm"
										@class={{unless @model.favorite "gray"}}
									/><ToolTipComponent @text={{@model.favoriteText}} />
								</div>
							{{/unless}}
							<div
								role="button"
								{{on "click" this.openAdditionalOptions}}
								class="context"
							>
								<CommonUiIconComponent @name="Ellipsis" @size="lg" />
							</div>
						</div>
					{{/if}}
				</div>

				<div class="info">
					<div class="anti-stretch">

						<EditableModelPropertyComponent
							@model={{@model}}
							@canEdit={{this.canEdit}}
							@editableProperty="title"
							@trackingObj={{this.trackingObj}}
						/>

					</div>
				</div>

				<div class="bottom">
					<OwnerSelectComponent @model={{@model}} @canEdit={{this.canCurrentUserEdit}} />
					<TagContainerComponent @model={{@model}} @canEdit={{this.canCurrentUserEdit}} />
				</div>

			{{else}}
				<div class="info" {{visibleCheckItem this.visibleChanged this.checkVisibility}}>
					<div class="anti-stretch">&nbsp;</div>
				</div>
			{{/if}}

		</li>
	</template>
}
